import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { CaretLeft as CaretLeftIcon } from "@phosphor-icons/react/CaretLeft";
import { CaretRight as CaretRightIcon } from "@phosphor-icons/react/CaretRight";
import { Quotes as QuoteIcon } from "@phosphor-icons/react/Quotes";
import useEmblaCarousel from "embla-carousel-react";
import * as React from "react";
import { sectionSpacing } from "./section-spacing";
import { headingFontFamily } from "../heading-font";

import type { EmblaCarouselType } from "embla-carousel";

export interface Review {
	id: string;
	author: string;
	title: string;
	logo?: string;
	comment: string;
}

export interface TestimonialsContent {
	eyebrow: string;
	icon: React.ReactElement;
	reviews: Review[];
}

export function Testimonials({ content }: { content: TestimonialsContent }) {
	const { eyebrow, icon, reviews } = content;

	const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
	const [prevBtnDisabled, setPrevBtnDisabled] = React.useState(true);
	const [nextBtnDisabled, setNextBtnDisabled] = React.useState(true);
	const [selectedIndex, setSelectedIndex] = React.useState(0);
	const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([]);

	const scrollPrev = React.useCallback(() => {
		emblaApi?.scrollPrev();
	}, [emblaApi]);

	const scrollNext = React.useCallback(() => {
		emblaApi?.scrollNext();
	}, [emblaApi]);

	const scrollTo = React.useCallback(
		(index: number) => {
			emblaApi?.scrollTo(index);
		},
		[emblaApi],
	);

	const onInit = React.useCallback((api: EmblaCarouselType) => {
		setScrollSnaps(api.scrollSnapList());
	}, []);

	const onSelect = React.useCallback((api: EmblaCarouselType) => {
		setSelectedIndex(api.selectedScrollSnap());
		setPrevBtnDisabled(!api.canScrollPrev());
		setNextBtnDisabled(!api.canScrollNext());
	}, []);

	React.useEffect(() => {
		if (!emblaApi) return;

		onInit(emblaApi);
		onSelect(emblaApi);
		emblaApi.on("reInit", onInit);
		emblaApi.on("reInit", onSelect);
		emblaApi.on("select", onSelect);
	}, [emblaApi, onInit, onSelect]);

	return (
		<Box
			sx={{
				bgcolor: "var(--mui-palette-background-level0)",
				py: sectionSpacing,
			}}>
			<Stack
				spacing={6}
				sx={{ maxWidth: "1200px", mx: "auto", px: 3 }}>
				<Stack
					spacing={2}
					sx={{ textAlign: "center" }}>
					<Box sx={{ display: "flex", justifyContent: "center" }}>
						<Chip
							color="secondary"
							icon={icon}
							label={eyebrow}
							size="small"
							variant="soft"
						/>
					</Box>
				</Stack>
				<Box sx={{ position: "relative" }}>
					<Box
						ref={emblaRef}
						sx={{
							overflow: "hidden",
							borderRadius: 2,
							pb: 3,
							pt: 1,
						}}>
						<Box
							sx={{
								display: "flex",
								touchAction: "pan-y",
							}}>
							{reviews.map((review) => (
								<Box
									key={review.id}
									sx={{
										flex: "0 0 100%",
										minWidth: 0,
										position: "relative",
										pb: 1,
										px: { xs: 6, sm: 8 },
									}}>
									<Stack
										spacing={4}
										sx={{
											alignItems: "center",
											textAlign: "center",
											maxWidth: 720,
											mx: "auto",
										}}>
										<QuoteIcon
											color="var(--mui-palette-secondary-main)"
											size={40}
											weight="fill"
										/>
										<Typography
											sx={{ fontFamily: headingFontFamily }}
											variant="h4">
											&ldquo;{review.comment}&rdquo;
										</Typography>
										<Stack
											spacing={1.5}
											sx={{ alignItems: "center" }}>
											{review.logo ? (
												<Box
													alt={`${review.author} logo`}
													component="img"
													src={review.logo}
													sx={{
														height: 28,
														maxWidth: 160,
														objectFit: "contain",
													}}
												/>
											) : null}
											<Stack
												spacing={0}
												sx={{ alignItems: "center" }}>
												<Typography
													color="text.primary"
													variant="subtitle2">
													{review.author}
												</Typography>
												<Typography
													color="text.secondary"
													variant="body2">
													{review.title}
												</Typography>
											</Stack>
										</Stack>
									</Stack>
								</Box>
							))}
						</Box>
					</Box>
					<Box
						sx={{
							position: "absolute",
							top: "50%",
							transform: "translateY(-50%)",
							left: 0,
							zIndex: 1,
						}}>
						<IconButton
							disabled={prevBtnDisabled}
							onClick={scrollPrev}
							sx={{
								bgcolor: "background.paper",
								boxShadow: 2,
								"&:hover": { bgcolor: "background.paper" },
							}}>
							<CaretLeftIcon weight="bold" />
						</IconButton>
					</Box>
					<Box
						sx={{
							position: "absolute",
							top: "50%",
							transform: "translateY(-50%)",
							right: 0,
							zIndex: 1,
						}}>
						<IconButton
							disabled={nextBtnDisabled}
							onClick={scrollNext}
							sx={{
								bgcolor: "background.paper",
								boxShadow: 2,
								"&:hover": { bgcolor: "background.paper" },
							}}>
							<CaretRightIcon weight="bold" />
						</IconButton>
					</Box>
				</Box>
				<Stack
					direction="row"
					spacing={1}
					sx={{ justifyContent: "center" }}>
					{scrollSnaps.map((_, index) => (
						<Box
							// eslint-disable-next-line react/no-array-index-key -- index is stable and tied to slide position
							key={index}
							onClick={() => {
								scrollTo(index);
							}}
							sx={{
								bgcolor:
									index === selectedIndex
										? "secondary.main"
										: "action.selected",
								borderRadius: "50%",
								cursor: "pointer",
								height: "10px",
								width: "10px",
								transition: "all .2s ease",
								"&:hover": {
									bgcolor: "secondary.light",
								},
							}}
						/>
					))}
				</Stack>
			</Stack>
		</Box>
	);
}
